/**
 * Origin-scoped store for thumbnails the studio captured itself.
 *
 * Captures are produced from the studio's OWN render of a project (see
 * `project-thumbnail-capture.ts`), so unlike the platform's Browserless path
 * they work for drafts — which is every project until it is published.
 *
 * Bytes live in IndexedDB on the studio origin and are handed to cards as
 * `blob:` object URLs. A blob URL is same-origin by construction, so this
 * delivery path cannot reintroduce the COEP failure #1268 fixed.
 *
 * Everything degrades to "no thumbnail" rather than throwing: a browser with
 * IndexedDB blocked (private mode, jsdom in unit tests) simply keeps showing
 * the honest placeholder.
 */

const DB_NAME = 'cogo-studio-thumbnails'
const DB_VERSION = 1
const STORE = 'project-thumbnails'

export interface StoredProjectThumbnail {
  projectId: string
  blob: Blob
  width: number
  height: number
  capturedAt: number
  /** How the bytes were produced — recorded so a future server-side producer is distinguishable. */
  source: 'studio-canvas-capture'
}

type Listener = (projectId: string) => void

const listeners = new Set<Listener>()
const objectUrls = new Map<string, string>()

function hasIndexedDb(): boolean {
  return typeof indexedDB !== 'undefined' && indexedDB !== null
}

function openDb(): Promise<IDBDatabase | null> {
  if (!hasIndexedDb()) return Promise.resolve(null)
  return new Promise((resolve) => {
    let request: IDBOpenDBRequest
    try {
      request = indexedDB.open(DB_NAME, DB_VERSION)
    } catch {
      resolve(null)
      return
    }
    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'projectId' })
      }
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => resolve(null)
    request.onblocked = () => resolve(null)
  })
}

function runRequest<T>(request: IDBRequest<T>): Promise<T | null> {
  return new Promise((resolve) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => resolve(null)
  })
}

/** Persist a captured thumbnail. Returns false when storage is unavailable. */
export async function putProjectThumbnail(
  entry: Omit<StoredProjectThumbnail, 'capturedAt' | 'source'> &
    Partial<Pick<StoredProjectThumbnail, 'capturedAt' | 'source'>>,
): Promise<boolean> {
  const db = await openDb()
  if (!db) return false
  const record: StoredProjectThumbnail = {
    capturedAt: Date.now(),
    source: 'studio-canvas-capture',
    ...entry,
  }
  const ok = await new Promise<boolean>((resolve) => {
    try {
      const tx = db.transaction(STORE, 'readwrite')
      tx.objectStore(STORE).put(record)
      tx.oncomplete = () => resolve(true)
      tx.onerror = () => resolve(false)
      tx.onabort = () => resolve(false)
    } catch {
      resolve(false)
    }
  })
  db.close()
  if (!ok) return false

  // Drop any stale object URL so the next read re-mints one for the new bytes.
  const previous = objectUrls.get(record.projectId)
  if (previous) {
    URL.revokeObjectURL(previous)
    objectUrls.delete(record.projectId)
  }
  for (const listener of listeners) listener(record.projectId)
  return true
}

export async function getProjectThumbnail(
  projectId: string,
): Promise<StoredProjectThumbnail | null> {
  const db = await openDb()
  if (!db) return null
  let result: StoredProjectThumbnail | null = null
  try {
    const tx = db.transaction(STORE, 'readonly')
    result = (await runRequest(
      tx.objectStore(STORE).get(projectId) as IDBRequest<StoredProjectThumbnail>,
    )) ?? null
  } catch {
    result = null
  }
  db.close()
  return result
}

/**
 * A `blob:` URL for the locally captured thumbnail, or `null` when there is
 * none. URLs are memoised per project so repeated card renders do not leak a
 * new blob URL each time.
 */
export async function getProjectThumbnailObjectUrl(
  projectId: string,
): Promise<string | null> {
  const cached = objectUrls.get(projectId)
  if (cached) return cached
  const stored = await getProjectThumbnail(projectId)
  if (!stored?.blob) return null
  if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') return null
  const url = URL.createObjectURL(stored.blob)
  objectUrls.set(projectId, url)
  return url
}

/** Subscribe to "a project's local thumbnail changed" notifications. */
export function subscribeProjectThumbnails(listener: Listener): () => void {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}

/** Test seam — drops memoised object URLs (does not touch IndexedDB). */
export function resetProjectThumbnailObjectUrlCache(): void {
  for (const url of objectUrls.values()) {
    try { URL.revokeObjectURL(url) } catch { /* best effort */ }
  }
  objectUrls.clear()
}
